import { useMemo } from 'react';
import {
  Button,
  HStack,
  VStack,
  IconButton,
  Text,
  Box,
  Progress,
  Badge,
  SimpleGrid,
  Card,
  CardBody,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  useColorModeValue,
} from '@chakra-ui/react';
import { FiPlus, FiTrash2, FiEye, FiUpload, FiRefreshCw, FiUsers, FiClock } from 'react-icons/fi';
import { ColumnDef } from '@tanstack/react-table';
import { DataTable } from '@/design-system/components/DataTable';
import { PageContainer } from '@/client/components/layout/PageContainer';
import { ConfirmDialog } from '@/design-system/components/ConfirmDialog';
import { EventTaskDrawer } from '../components/EventTaskDrawer';
import { EventCreateDrawer } from '../components/EventCreateDrawer';
import { useUserBotsPage } from '../hooks';
import type { UserBot } from '@/lib/sdk/adhoc/types';

function relativeTime(iso?: string): string {
  if (!iso) return '—';
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

function SegmentedProgressBar({ completed, inProgress, pending, total }: {
  completed: number; inProgress: number; pending: number; total: number;
}) {
  const completedPct  = total > 0 ? (completed / total) * 100 : 0;
  const inProgressPct = total > 0 ? (inProgress / total) * 100 : 0;
  const pendingPct    = total > 0 ? (pending / total) * 100 : 0;
  const trackBg = useColorModeValue('gray.100', 'gray.700');

  return (
    <Box minW="160px">
      <Box h="6px" borderRadius="full" bg={trackBg} overflow="hidden" display="flex" mb={2}>
        <Box w={`${completedPct}%`} bg="green.400" transition="width 0.3s" />
        <Box w={`${inProgressPct}%`} bg="orange.300" transition="width 0.3s" />
        <Box w={`${pendingPct}%`} bg="gray.300" transition="width 0.3s" />
      </Box>
      <VStack spacing={0} align="stretch">
        <HStack justify="space-between">
          <HStack spacing={1}><Box w="8px" h="8px" borderRadius="full" bg="green.400" /><Text fontSize="xs" color="text-secondary">Done</Text></HStack>
          <Text fontSize="xs" fontWeight="medium">{completed} <Text as="span" color="text-muted">({Math.round(completedPct)}%)</Text></Text>
        </HStack>
        {inProgress > 0 && (
          <HStack justify="space-between">
            <HStack spacing={1}><Box w="8px" h="8px" borderRadius="full" bg="orange.300" /><Text fontSize="xs" color="text-secondary">WIP</Text></HStack>
            <Text fontSize="xs" fontWeight="medium">{inProgress} <Text as="span" color="text-muted">({Math.round(inProgressPct)}%)</Text></Text>
          </HStack>
        )}
        <HStack justify="space-between">
          <HStack spacing={1}><Box w="8px" h="8px" borderRadius="full" bg="gray.300" /><Text fontSize="xs" color="text-secondary">Pending</Text></HStack>
          <Text fontSize="xs" fontWeight="medium">{pending} <Text as="span" color="text-muted">({Math.round(pendingPct)}%)</Text></Text>
        </HStack>
        <Text fontSize="xs" color="text-muted" textAlign="center" pt={1}>{completed}/{total} total</Text>
      </VStack>
    </Box>
  );
}

const LIFECYCLE_LABELS: Record<string, string> = {
  daily: 'Daily',
  weekly: 'Weekly',
  monthly: 'Monthly',
  long_running: 'Long Running',
};

const LIFECYCLE_COLORS: Record<string, string> = {
  daily: 'blue',
  weekly: 'purple',
  monthly: 'teal',
  long_running: 'gray',
};

export type EventsPanelVariant = 'instant' | 'checklist';

// EventsPanel holds the stats grid, table, and dialogs shared across the
// Tasks page's three tabs (#681/#721 Phase 7c, extended for the 3-way
// Team Tasks / Checklists / Others split) — pass includeSop to also
// aggregate SOP-template bots, not just adhoc-enabled ones, and
// variant="checklist" to narrow the table to just the SOP-only subset
// (tagged by lifecycle) with checklist-appropriate actions instead of
// the instant-task create/add-tasks/delete flow.
export function EventsPanel({ includeSop, variant = 'instant' }: { includeSop?: boolean; variant?: EventsPanelVariant } = {}) {
  const isChecklist = variant === 'checklist';
  const p = useUserBotsPage(includeSop, isChecklist);

  const columns = useMemo<ColumnDef<UserBot>[]>(
    () => [
      {
        accessorKey: 'title',
        header: 'Tasks Group Name',
        cell: ({ row }) => (
          <Text fontWeight="bold" fontSize="md" color="brand.500" cursor="pointer" _hover={{ textDecoration: 'underline' }}
            data-testid={`ev-row-title-${row.original.short_code}`}
            onClick={() => p.navigate(`/dashboard/${p.orgId}/workflows/${row.original.short_code}/execute?source_key=${row.original.source_key}`)}>
            {row.original.title}
          </Text>
        ),
        size: 200,
      },
      ...(isChecklist ? [{
        id: 'lifecycle',
        header: 'Lifecycle',
        cell: ({ row }: { row: { original: UserBot } }) => {
          const lifecycle = row.original.task_lifecycle ?? 'daily';
          return (
            <Badge colorScheme={LIFECYCLE_COLORS[lifecycle] ?? 'gray'} fontSize="xs">
              {LIFECYCLE_LABELS[lifecycle] ?? lifecycle}
            </Badge>
          );
        },
        size: 110,
      } as ColumnDef<UserBot>] : []),
      {
        id: 'progress',
        header: 'Progress',
        cell: ({ row }) => {
          const completed = row.original.completed_count ?? 0;
          const inProgress = row.original.in_progress_count ?? 0;
          const total = row.original.total_count ?? 0;
          const pending = Math.max(0, (row.original.pending_count ?? 0) - inProgress);
          return <SegmentedProgressBar completed={completed} inProgress={inProgress} pending={pending} total={total} />;
        },
        size: 200,
      },
      {
        id: 'delayed',
        header: 'Delayed',
        cell: ({ row }) => {
          const overdue = row.original.overdue_count ?? 0;
          const dueToday = row.original.due_today_count ?? 0;
          if (overdue === 0 && dueToday === 0) return <Text color="text-muted">—</Text>;
          return (
            <VStack spacing={1} align="flex-start">
              {overdue > 0 && <Badge colorScheme="red" fontSize="xs">{overdue} overdue</Badge>}
              {dueToday > 0 && <Badge colorScheme="yellow" fontSize="xs">{dueToday} due today</Badge>}
            </VStack>
          );
        },
        size: 130,
      },
      {
        id: 'today',
        header: 'Today',
        cell: ({ row }) => {
          const count = row.original.completed_today_count ?? 0;
          if (count === 0) return <Text color="text-muted">—</Text>;
          return <Badge colorScheme="green" fontSize="xs">{count} done</Badge>;
        },
        size: 100,
      },
      {
        id: 'team',
        header: 'Team',
        cell: ({ row }) => {
          const count = row.original.assignee_count ?? 0;
          if (count === 0) return <Text color="text-muted">—</Text>;
          return <HStack spacing={1}><FiUsers size={13} /><Text fontSize="sm">{count}</Text></HStack>;
        },
        size: 80,
      },
      {
        id: 'last_activity',
        header: 'Last Activity',
        cell: ({ row }) => (
          <HStack spacing={1} color="text-muted">
            <FiClock size={12} />
            <Text fontSize="xs">{relativeTime(row.original.last_activity_at)}</Text>
          </HStack>
        ),
        size: 120,
      },
      {
        id: 'actions',
        header: 'Actions',
        cell: ({ row }) => {
          const event = row.original;
          return (
            <HStack spacing={1}>
              <IconButton aria-label="View tasks" icon={<FiEye />} size="sm" variant="ghost"
                data-testid={`ev-row-view-btn-${event.short_code}`}
                onClick={() => p.navigate(`/dashboard/${p.orgId}/workflows/${event.short_code}/execute?source_key=${event.source_key}`)} />
              {/* Checklist bots are created/edited/deleted via the workflow
                  builder, not this page's bulk-upload/delete-event flow — so
                  those actions only apply to instant-task bots. */}
              {!isChecklist && p.canEdit && (
                <IconButton aria-label="Add tasks" icon={<FiUpload />} size="sm" variant="ghost"
                  data-testid={`ev-row-add-tasks-btn-${event.short_code}`}
                  onClick={() => p.setAddTasksBot(event)} />
              )}
              {!isChecklist && p.canDelete && (
                <IconButton aria-label="Delete event" icon={<FiTrash2 />} size="sm" variant="ghost" colorScheme="red"
                  data-testid={`ev-row-delete-btn-${event.short_code}`}
                  onClick={() => p.handleDelete(event)} />
              )}
            </HStack>
          );
        },
        size: 110,
      },
    ],
    [p.orgId, p.navigate, p.handleDelete, p.canEdit, p.canDelete, isChecklist]
  );

  const s = p.summaryStats;

  return (
    <>
      <HStack justify="flex-end" spacing={2} mb={4}>
        <IconButton aria-label="Refresh events" icon={<FiRefreshCw />} variant="outline"
          data-testid="ev-refresh-btn"
          onClick={() => p.refetch()} isLoading={p.isRefetching} />
        {/* view_instant gates visibility of the button (harmless no-op on the
            standalone Events route, which already requires view_instant to
            reach at all — this matters once this panel is embedded in the
            unified Tasks page's SOP+Adhoc tab, which is ambient-view and
            doesn't itself require view_instant). add still gates the actual
            create action, unchanged. Checklist bots are created via the
            workflow builder, not this drawer, so this never shows for that
            variant regardless of permissions. */}
        {!isChecklist && p.canAdd && p.canViewInstant && (
          <Button leftIcon={<FiPlus />} colorScheme="brand" data-testid="ev-create-btn" onClick={() => p.setIsCreateOpen(true)}>
            Add Tasks
          </Button>
        )}
      </HStack>

      {p.events.length > 0 && (
          <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4} mb={6}>
            <Card bgGradient={p.cardGradient} data-testid="ev-stat-overall">
              <CardBody py={4} px={5}>
                <Stat>
                  <StatLabel fontSize="xs" color="text-secondary">Overall Completion</StatLabel>
                  <StatNumber fontSize="2xl" color="brand.500">{s.overallPct}%</StatNumber>
                  <StatHelpText mb={0}><Text fontSize="xs" color="text-muted">{s.totalCompleted}/{s.totalTasks} tasks</Text></StatHelpText>
                  <Progress value={s.overallPct} size="xs" colorScheme="brand" borderRadius="full" mt={2} />
                </Stat>
              </CardBody>
            </Card>
            <Card bgGradient={p.cardGradient} data-testid="ev-stat-overdue">
              <CardBody py={4} px={5}>
                <Stat>
                  <StatLabel fontSize="xs" color="text-secondary">Overdue</StatLabel>
                  <StatNumber fontSize="2xl" color={s.totalOverdue > 0 ? 'red.500' : 'text-primary'}>{s.totalOverdue}</StatNumber>
                  <StatHelpText mb={0}><Text fontSize="xs" color="text-muted">{s.overdueEventCount === 0 ? 'no events affected' : `across ${s.overdueEventCount} event${s.overdueEventCount !== 1 ? 's' : ''}`}</Text></StatHelpText>
                </Stat>
              </CardBody>
            </Card>
            <Card bgGradient={p.cardGradient} data-testid="ev-stat-due-today">
              <CardBody py={4} px={5}>
                <Stat>
                  <StatLabel fontSize="xs" color="text-secondary">Due Today</StatLabel>
                  <StatNumber fontSize="2xl" color={s.totalDueToday > 0 ? 'orange.400' : 'text-primary'}>{s.totalDueToday}</StatNumber>
                  <StatHelpText mb={0}><Text fontSize="xs" color="text-muted">{s.dueTodayEventCount === 0 ? 'nothing due today' : `across ${s.dueTodayEventCount} event${s.dueTodayEventCount !== 1 ? 's' : ''}`}</Text></StatHelpText>
                </Stat>
              </CardBody>
            </Card>
            <Card bgGradient={p.cardGradient} data-testid="ev-stat-completed-today">
              <CardBody py={4} px={5}>
                <Stat>
                  <StatLabel fontSize="xs" color="text-secondary">Completed Today</StatLabel>
                  <StatNumber fontSize="2xl" color={s.totalCompletedToday > 0 ? 'green.500' : 'text-primary'}>{s.totalCompletedToday}</StatNumber>
                  <StatHelpText mb={0}><Text fontSize="xs" color="text-muted">{s.completedTodayEventCount === 0 ? 'no activity yet' : `across ${s.completedTodayEventCount} event${s.completedTodayEventCount !== 1 ? 's' : ''}`}</Text></StatHelpText>
                </Stat>
              </CardBody>
            </Card>
          </SimpleGrid>
        )}

      <DataTable
        data={p.events} columns={columns} isLoading={p.isLoading}
        emptyMessage={isChecklist ? 'No checklists found.' : 'No events found. Create your first event.'}
        enableSorting enableFiltering enablePagination pageSize={20}
      />

      {!isChecklist && (
        <>
          <ConfirmDialog
            isOpen={p.isDeleteOpen} onClose={p.onDeleteClose} onConfirm={p.handleDeleteConfirm}
            title="Delete Event"
            message={`Delete "${p.deleteTarget?.title}"? This will permanently cancel all associated tasks and cannot be undone.`}
            confirmText="Delete Event" isLoading={p.isPendingDelete}
          />

          {p.addTasksBot && (
            <EventTaskDrawer
              isOpen={true} onClose={() => p.setAddTasksBot(null)}
              orgId={p.orgId} event={p.addTasksBot} teamMembers={p.teamMembers} onSaved={() => p.refetch()}
            />
          )}

          <EventCreateDrawer
            isOpen={p.isCreateOpen} onClose={() => p.setIsCreateOpen(false)}
            orgId={p.orgId} teamMembers={p.teamMembers} onSaved={() => p.refetch()}
          />
        </>
      )}
    </>
  );
}

export function UserBotsPage() {
  return (
    <PageContainer title="Events">
      <EventsPanel />
    </PageContainer>
  );
}
